import React, { useState, useEffect } from 'react';
import {
  HashRouter as Router,
  Routes,
  Route,
  useNavigate,
  useParams,
  useLocation,
} from 'react-router';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Navigation } from './components/Navigation';
import { AppSidebar } from './components/AppSidebar';
import {
  SidebarProvider,
  SidebarInset,
  useSidebar,
} from './components/ui/sidebar';
import { HomePage } from './components/HomePage';
import { MyVideosPage } from './components/MyVideosPage';
import { VideoPage } from './components/VideoPage';
import { ExplorePage } from './components/ExplorePage';
import { LoginDialog } from './components/LoginDialog';
import { PointsDialog, PointsTransaction } from './components/PointsDialog';
import { Toaster } from './components/ui/sonner';
import { E_LocalStorageKey, LocalStorageUtil } from './utils/LocalStorageUtil';
import { post, postData } from './utils/HttpUtils';
import { getUserInfo, sendGoogleIdToken } from './api/user';
import { TVideoStatus, VideoCard } from './model/home';
import { Loader2 } from 'lucide-react';
import { getVideoDetail } from './api/video';
import { toast } from 'sonner';
import { VideoDetail } from './model/video';
import { IUserInfo } from './model/user';
import { cancelGenerate, reGenerate } from './api/home';
import { Theme } from '@radix-ui/themes';
import Privacy from './components/Privacy';
import Service from './components/Service';
import VideoExample from './components/VideoExample';
import { getMySubjectByVideo } from './utils/VideoUtils';
import { getMyVideoList } from './api/myclips';
import { mySubjectColors } from './consts/subject';

let isGooleLoaded = false;
interface Video {
  id: string;
  question: string;
  status: 'generating' | 'complete';
  thumbnail: string;
  createdAt: Date;
  subject:
    | 'Mathematics'
    | 'Physics'
    | 'Biology'
    | 'Chemistry'
    | 'History'
    | 'Geography'
    | 'Psychology'
    | 'Computer Science'
    | 'AI'
    | 'Economics'
    | 'Literature'
    | 'Engineering';
  progress: number; // 0-100
  videoCount: number;
  isFavorite?: boolean;
  duration?: number; // in seconds
  watchProgress?: number; // 0-100
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useSidebar();
  console.log('location', location);
  const [userid, setUserId] = useState<string | undefined | null>(null);
  const [userInfo, setUserInfo] = useState<IUserInfo | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [userPoints, setUserPoints] = useState(1250);
  // const [isLoggedIn, setIsLoggedIn] = useState(false);
  // 视频生成完成后需要刷新
  const [needUpdate, setNeedUpdate] = useState<boolean>(false);
  const isLoggedIn = !!userid;
  // const [userEmail, setUserEmail] = useState<string>("user@example.com");
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showPointsDialog, setShowPointsDialog] = useState(false);
  const [lastClaimDate, setLastClaimDate] = useState<string | null>(null);
  const [pointsTransactions, setPointsTransactions] = useState<
    PointsTransaction[]
  >([]);
  const [subjectCounts, setSubjectCounts] = useState<
    { name: string; count: number }[]
  >([]);

  useEffect(() => {
    const fetchStats = async () => {
      if (!userid) {
        setSubjectCounts([]);
        return;
      }
      try {
        // Fetch all videos for stats
        const res = await getMyVideoList('', '', 'Newest');
        const cards = res?.cards || [];

        const counts: Record<string, number> = {};
        cards.forEach((video) => {
          // Only count successful or processing videos if desired, but PRD implies "personal videos" which usually means all non-deleted.
          // Let's match MyVideosPage logic which shows everything.
          const [subjectName] = getMySubjectByVideo(video);
          if (subjectName) {
            counts[subjectName] = (counts[subjectName] || 0) + 1;
          }
        });

        const stats = Object.entries(counts)
          .map(([name, count]) => ({ name, count }))
          .sort((a, b) => b.count - a.count)
          .filter((item) => !!mySubjectColors[item.name]); // Sort by count descending

        setSubjectCounts(stats);
      } catch (e) {
        console.error('Failed to fetch video stats', e);
      }
    };

    fetchStats();
  }, [userid, needUpdate]);

  const handleCredentialResponse = async (response: any) => {
    try {
      // 将收到的ID Token发送到你的后端服务器进行验证
      const idToken = response.credential;
      // console.log('google id tooken:', idToken)
      const res = await sendGoogleIdToken(idToken);
      if (res.jwt_token) {
        handleLogin(res.jwt_token, res.user);
      }
    } catch (e) {
      toast.error(e.message || 'login error');
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const myid = LocalStorageUtil.getLocalStorage(
          E_LocalStorageKey.userDeviceId,
        );
        if (myid) {
          setUserId(myid);
        } else {
          setUserId(null);
          setUserInfo(null);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        if (userid) {
          const info = await getUserInfo();
          if (info?.user?.user_account_type !== 0) {
            setUserInfo(info.user);
          } else {
            LocalStorageUtil.removeLocalStorage(E_LocalStorageKey.userDeviceId);
            setUserId(null);
            setUserInfo(null);
          }
        } else {
          setUserInfo(null);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, [userid]);
  // Initialize with default videos and transactions
  useEffect(() => {
    const defaultVideos: Video[] = [
      {
        id: 'default-1',
        question:
          'How to solve quadratic equations using the quadratic formula?',
        status: 'complete',
        thumbnail: '',
        createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
        subject: 'Mathematics',
        progress: 100,
        videoCount: 4,
        duration: 52,
        watchProgress: 0,
      },
      {
        id: 'default-2',
        question:
          'Understanding cognitive behavioral therapy and its applications',
        status: 'complete',
        thumbnail: '',
        createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
        subject: 'Psychology',
        progress: 100,
        videoCount: 3,
        duration: 47,
        watchProgress: 73,
      },
      {
        id: 'default-3',
        question: 'Exploring the fundamentals of machine learning algorithms',
        status: 'complete',
        thumbnail: '',
        createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
        subject: 'AI',
        progress: 100,
        videoCount: 5,
        duration: 58,
        watchProgress: 100, // Fully watched
      },
    ];
    setVideos(defaultVideos);

    // Initialize with some transaction history
    const defaultTransactions: PointsTransaction[] = [
      {
        id: 'trans-1',
        type: 'earn',
        amount: 30,
        description: 'Daily check-in reward',
        date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      },
      {
        id: 'trans-2',
        type: 'spend',
        amount: 10,
        description: 'Clip generate',
        date: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
      },
      {
        id: 'trans-3',
        type: 'spend',
        amount: 10,
        description: 'Clip generate',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      },
      {
        id: 'trans-4',
        type: 'earn',
        amount: 30,
        description: 'Daily check-in reward',
        date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      },
      {
        id: 'trans-5',
        type: 'spend',
        amount: 10,
        description: 'Clip generate',
        date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
      },
    ];
    setPointsTransactions(defaultTransactions);
  }, []);

  const handleReGenerate = async (vid: string) => {
    try {
      await reGenerate(vid);
      setNeedUpdate(true);
    } catch (e) {
      toast.error(e.message || 'request fail');
    }
  };
  const handleCancelGenerate = async (vid: string) => {
    try {
      await cancelGenerate(vid);
      setNeedUpdate(true);
    } catch (e) {
      toast.error(e.message || 'request fail');
    }
  };
  const handleGenerate = (qVideos: VideoCard) => {
    const subjects: Video['subject'][] = [
      'Mathematics',
      'Physics',
      'Biology',
      'Chemistry',
      'History',
      'Geography',
      'Psychology',
      'Computer Science',
      'AI',
      'Economics',
      'Literature',
      'Engineering',
    ];
    const randomSubject = subjects[Math.floor(Math.random() * subjects.length)];

    const newVideo: Video = {
      id: `video-${Date.now()}`,
      question: '',
      status: 'generating',
      thumbnail: '',
      createdAt: new Date(),
      subject: randomSubject,
      progress: 0,
      videoCount: Math.floor(Math.random() * 3) + 3, // 3-5 videos
      duration: Math.floor(Math.random() * 21) + 40, // 40-60 seconds
      watchProgress: 0,
    };

    setVideos((prev) => [newVideo, ...prev]);
    setUserPoints((prev) => prev - 10);

    // Add transaction record
    const transaction: PointsTransaction = {
      id: `trans-${Date.now()}`,
      type: 'spend',
      amount: 10,
      description: 'Clip generate',
      date: new Date(),
    };
    setPointsTransactions((prev) => [transaction, ...prev]);
  };

  const handleVideoClick = (videoId: string) => {
    navigate(`/video/${videoId}`);
  };

  const handleUpdateVideoStatus = (videoId: string, status: TVideoStatus) => {
    // setVideos((prev) =>
    //   prev.map((video) =>
    //     video.id === videoId ? { ...video, status, progress: 100 } : video
    //   )
    // );
  };

  const handleUpdateVideoProgress = (videoId: string, progress: number) => {
    setVideos((prev) =>
      prev.map((video) =>
        video.id === videoId ? { ...video, progress } : video,
      ),
    );
  };

  const handleDeleteVideo = (videoId: string) => {
    setVideos((prev) => prev.filter((video) => video.id !== videoId));
  };

  const handleToggleFavorite = (videoId: string) => {
    setVideos((prev) =>
      prev.map((video) =>
        video.id === videoId
          ? { ...video, isFavorite: !video.isFavorite }
          : video,
      ),
    );
  };

  const handleLogin = (token: string, user: IUserInfo) => {
    // setIsLoggedIn(true);
    setUserId(token);
    // setUserEmail(user.email);
    setUserInfo(user);
    setShowLoginDialog(false);
    LocalStorageUtil.setLocalStorage(E_LocalStorageKey.userDeviceId, token);
  };

  const handleLogout = () => {
    // setIsLoggedIn(false);
    setUserInfo(null);
    setUserId(null);
    // setUserEmail("");
    navigate('/');
    if (LocalStorageUtil.getLocalStorage(E_LocalStorageKey.userDeviceId)) {
      LocalStorageUtil.removeLocalStorage(E_LocalStorageKey.userDeviceId);
    }
  };

  const canClaimDailyPoints = () => {
    if (!lastClaimDate) return true;
    const today = new Date().toDateString();
    return lastClaimDate !== today;
  };

  const handleClaimDailyPoints = () => {
    if (!canClaimDailyPoints()) return;

    const today = new Date().toDateString();
    setLastClaimDate(today);
    setUserPoints((prev) => prev + 30);

    // Add transaction record
    const transaction: PointsTransaction = {
      id: `trans-${Date.now()}`,
      type: 'earn',
      amount: 30,
      description: 'Daily check-in reward',
      date: new Date(),
    };
    setPointsTransactions((prev) => [transaction, ...prev]);
  };
  const isPolicy =
    location.pathname.includes('privacy') ||
    location.pathname.includes('service') ||
    location.pathname.includes('video-example');

  return isPolicy ? (
    <div
      className="min-h-screen bg-background"
      onDrop={(e) => {
        e.preventDefault();
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
    >
      {isPolicy ? null : (
        <Navigation
          userPoints={userPoints}
          userInfo={userInfo}
          onShowLogin={() => setShowLoginDialog(true)}
          // userEmail={userEmail}
          // isLoggedIn={isLoggedIn}
          logout={handleLogout}
          // handleCredentialResponse={handleCredentialResponse}
          onOpenPointsDialog={() => setShowPointsDialog(true)}
        />
      )}

      <Routes>
        <Route
          index
          path="/"
          element={
            <HomePage
              onGenerate={handleGenerate}
              videos={videos}
              userid={userid}
              updateFlag={needUpdate}
              onVideoClick={(id: string) => handleVideoClick(id || 'v_0')}
              onUpdateVideoStatus={handleUpdateVideoStatus}
              onDeleteVideo={handleDeleteVideo}
              onToggleFavorite={handleToggleFavorite}
              onNavigateToExplore={() => navigate('/explore')}
              onNavigateToMyVideos={() => navigate('/my-clips')}
              isLoggedIn={isLoggedIn}
              onShowLogin={() => setShowLoginDialog(true)}
              onReGenerate={handleReGenerate}
              onCancelGenerate={handleCancelGenerate}
              handleHasUpdated={() => setNeedUpdate(false)}
            />
          }
        />

        <Route
          path="/my-clips"
          element={
            <MyVideosPage
              userid={userid}
              videos={videos}
              updateFlag={needUpdate}
              onCancelGenerate={handleCancelGenerate}
              onVideoClick={handleVideoClick}
              onUpdateVideoStatus={handleUpdateVideoStatus}
              onUpdateVideoProgress={handleUpdateVideoProgress}
              onDeleteVideo={handleDeleteVideo}
              onToggleFavorite={handleToggleFavorite}
              onReGenerate={handleReGenerate}
              handleHasUpdated={() => setNeedUpdate(false)}
              onNavigateToHome={() => navigate('/')}
            />
          }
        />

        <Route
          path="/video/:id"
          element={
            <VideoPageWrapper
              userid={userid}
              onShowLogin={() => setShowLoginDialog(true)}
              videos={videos}
            />
          }
        />

        <Route
          path="/explore"
          element={
            <ExplorePage
              userid={userid}
              onVideoClick={handleVideoClick}
              onBackToMyVideos={() => navigate('/my-clips')}
            />
          }
        />

        <Route path="/privacy" element={<Privacy />} />
        <Route path="/service" element={<Service />} />
        <Route path="/video-example" element={<VideoExample />} />
      </Routes>

      {showLoginDialog && (
        <LoginDialog
          open={showLoginDialog}
          onLogin={handleLogin}
          handleCredentialResponse={handleCredentialResponse}
          onClose={() => setShowLoginDialog(false)}
        />
      )}

      <PointsDialog
        open={showPointsDialog}
        onClose={() => setShowPointsDialog(false)}
        currentPoints={userPoints}
        canClaimDaily={canClaimDailyPoints()}
        onClaimDaily={handleClaimDailyPoints}
        transactions={pointsTransactions}
      />

      <Toaster />
    </div>
  ) : (
    <div
      className="flex min-h-screen w-full bg-background"
      onDrop={(e) => {
        e.preventDefault();
      }}
      onDragOver={(e) => {
        e.preventDefault();
      }}
    >
      <AppSidebar
        userInfo={userInfo}
        subjectCounts={subjectCounts}
        onShowLogin={() => setShowLoginDialog(true)}
        onLogout={handleLogout}
      />
      <SidebarInset
        className={` ${state !== 'collapsed' ? 'home-right-content-width-sider' : 'home-right-content-no-sider'}`}
      >
        <Navigation
          userPoints={userPoints}
          userInfo={userInfo}
          onShowLogin={() => setShowLoginDialog(true)}
          // userEmail={userEmail}
          // isLoggedIn={isLoggedIn}
          logout={handleLogout}
          // handleCredentialResponse={handleCredentialResponse}
          onOpenPointsDialog={() => setShowPointsDialog(true)}
        />

        <Routes>
          <Route
            index
            path="/"
            element={
              <HomePage
                onGenerate={handleGenerate}
                videos={videos}
                userid={userid}
                updateFlag={needUpdate}
                onVideoClick={(id: string) => handleVideoClick(id || 'v_0')}
                onUpdateVideoStatus={handleUpdateVideoStatus}
                onDeleteVideo={handleDeleteVideo}
                onToggleFavorite={handleToggleFavorite}
                onNavigateToExplore={() => navigate('/explore')}
                onNavigateToMyVideos={() => navigate('/my-clips')}
                isLoggedIn={isLoggedIn}
                onShowLogin={() => setShowLoginDialog(true)}
                onReGenerate={handleReGenerate}
                onCancelGenerate={handleCancelGenerate}
                handleHasUpdated={() => setNeedUpdate(false)}
              />
            }
          />

          <Route
            path="/my-clips"
            element={
              <MyVideosPage
                userid={userid}
                videos={videos}
                updateFlag={needUpdate}
                onCancelGenerate={handleCancelGenerate}
                onVideoClick={handleVideoClick}
                onUpdateVideoStatus={handleUpdateVideoStatus}
                onUpdateVideoProgress={handleUpdateVideoProgress}
                onDeleteVideo={handleDeleteVideo}
                onToggleFavorite={handleToggleFavorite}
                onReGenerate={handleReGenerate}
                handleHasUpdated={() => setNeedUpdate(false)}
                onNavigateToHome={() => navigate('/')}
              />
            }
          />

          <Route
            path="/video/:id"
            element={
              <VideoPageWrapper
                userid={userid}
                onShowLogin={() => setShowLoginDialog(true)}
                videos={videos}
              />
            }
          />

          <Route
            path="/explore"
            element={
              <ExplorePage
                userid={userid}
                onVideoClick={handleVideoClick}
                onBackToMyVideos={() => navigate('/my-clips')}
              />
            }
          />
        </Routes>

        {showLoginDialog && (
          <LoginDialog
            open={showLoginDialog}
            onLogin={handleLogin}
            handleCredentialResponse={handleCredentialResponse}
            onClose={() => setShowLoginDialog(false)}
          />
        )}

        <PointsDialog
          open={showPointsDialog}
          onClose={() => setShowPointsDialog(false)}
          currentPoints={userPoints}
          canClaimDaily={canClaimDailyPoints()}
          onClaimDaily={handleClaimDailyPoints}
          transactions={pointsTransactions}
        />

        <Toaster />
      </SidebarInset>
    </div>
  );
}

function VideoPageWrapper({
  videos,
  userid,
  onShowLogin,
}: {
  videos: Video[];
  userid: string;
  onShowLogin: () => void;
}) {
  const { id } = useParams<{ id: string }>();
  const [videoDetail, setVideoDetail] = useState<VideoDetail>();
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const currentVideo = videos.find((v) => v.id === id);
  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const vDetail = await getVideoDetail(id);
          setVideoDetail(vDetail);
        }
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
        toast(e.message || 'get video info failed');
      }
    })();
  }, []);

  // For explore videos that aren't in the user's videos
  const isExploreVideo = id?.startsWith('explore-');
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 mb-8">
        <Loader2 className="w-12 h-12 text-black animate-spin mb-4" />
        <p className="text-muted-foreground">Query video details...</p>
      </div>
    );
  } else if (videoDetail) {
    return (
      <VideoPage
        videoDetail={videoDetail}
        videoId={id || ''}
        userid={userid}
        question={currentVideo?.question || 'Community Video'}
        onBack={() => navigate(-1)}
        onShare={() => navigate('/explore')}
        onShowLogin={onShowLogin}
      />
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl mb-4">Video not found</h2>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2 bg-primary text-black rounded-full"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
  // return (
  //   <VideoPage
  //     video={videoDetail}
  //     videoId={id || ""}
  //     question={currentVideo?.question || "Community Video"}
  //     onBack={() => navigate(isExploreVideo ? "/explore" : "/my-clips")}
  //     onShare={() => navigate("/explore")}
  //   />
  // );
}

export default function App() {
  return (
    <Router>
      <AuthProvider>
        <Theme className="font-alipuhui-r">
          <SidebarProvider>
            <AppContent />
          </SidebarProvider>
        </Theme>
      </AuthProvider>
    </Router>
  );
}
